import { useState } from 'react';
import { Plus, Target, Trophy, X } from 'lucide-react';
import type { MemberPublic, PostMetadataGame } from '@/lib/db/types';
import { GOLF_GAMES } from '@/lib/reference/golf-games';

interface Guest {
  id: string;
  name: string;
}

interface Props {
  games: PostMetadataGame[];
  setGames: (games: PostMetadataGame[]) => void;
  members: MemberPublic[];
  guests?: Guest[];
  onOpenGamesLib?: () => void;
}

function AddGame({
  members,
  guests = [],
  onCancel,
  onAdd,
  onOpenGamesLib,
}: {
  members: MemberPublic[];
  guests?: Guest[];
  onCancel: () => void;
  onAdd: (g: PostMetadataGame) => void;
  onOpenGamesLib?: () => void;
}) {
  const [type, setType] = useState<'nassau' | 'skins'>('nassau');
  const [scoring, setScoring] = useState<'net' | 'gross'>('net');
  const [amount, setAmount] = useState('5');
  const [opponentIds, setOpponentIds] = useState<string[]>([]);

  const toggleOpp = (id: string) => {
    setOpponentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div className="space-y-3 rounded-xl border border-line bg-paper p-4">
      <div className="flex gap-2">
        {(['nassau', 'skins'] as const).map((t) => (
          <button key={t} type="button" onClick={() => setType(t)} className={`flex-1 rounded-lg py-2 text-xs font-bold capitalize ${type === t ? 'bg-navy text-cream' : 'border border-line text-ink-soft'}`}>{t}</button>
        ))}
      </div>
      <div className="flex gap-2">
        {(['net', 'gross'] as const).map((s) => (
          <button key={s} type="button" onClick={() => setScoring(s)} className={`flex-1 rounded-lg py-2 text-xs font-bold uppercase ${scoring === s ? 'bg-gold text-navy' : 'border border-line text-ink-soft'}`}>{s}</button>
        ))}
      </div>
      <input
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className="w-full rounded-lg border border-line bg-cream-warm px-3 py-2 text-sm"
        placeholder="Wager amount"
      />
      <div className="text-[10px] font-bold uppercase tracking-wider text-gold-deep">Opponents</div>
      <div className="flex flex-wrap gap-1.5">
        {members.map((m) => (
          <button key={m.id} type="button" onClick={() => toggleOpp(m.id)} className={`rounded-full px-2.5 py-1 text-xs font-bold ${opponentIds.includes(m.id) ? 'bg-navy text-cream' : 'border border-line text-ink-soft'}`}>
            {m.name.split(' ')[0]}
          </button>
        ))}
        {guests.map((g) => (
          <button key={g.id} type="button" onClick={() => toggleOpp(g.id)} className={`rounded-full px-2.5 py-1 text-xs font-bold ${opponentIds.includes(g.id) ? 'bg-navy text-cream' : 'border border-line text-ink-soft'}`}>
            {g.name}
          </button>
        ))}
      </div>
      {onOpenGamesLib && (
        <button type="button" onClick={onOpenGamesLib} className="w-full text-xs font-bold text-gold-deep underline">
          Browse full games library →
        </button>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 rounded-lg border border-line py-2 text-sm font-semibold text-ink-soft">Cancel</button>
        <button
          type="button"
          disabled={!opponentIds.length || !amount}
          onClick={() => onAdd({ type, format_id: type, scoring, amount: Number(amount), opponentIds })}
          className="flex-1 rounded-lg bg-navy py-2 text-sm font-bold text-cream disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}

export function GamesEditor({ games, setGames, members, guests = [], onOpenGamesLib }: Props) {
  const [adding, setAdding] = useState(false);

  if (adding) {
    return (
      <AddGame
        members={members}
        guests={guests}
        onCancel={() => setAdding(false)}
        onAdd={(g) => { setGames([...games, g]); setAdding(false); }}
        onOpenGamesLib={onOpenGamesLib}
      />
    );
  }

  return (
    <div className="space-y-2">
      {games.length === 0 && (
        <div className="rounded border border-dashed border-line bg-paper p-4 text-center text-xs text-mute">
          No games on this round. Add a skins or Nassau game.
        </div>
      )}
      {games.map((g, i) => {
        const opps = (g.opponentIds || [])
          .map((id) => members.find((m) => m.id === id) || guests.find((gu) => gu.id === id))
          .filter(Boolean);
        return (
          <div key={i} className="flex items-center gap-3 rounded border border-gold bg-gold/10 p-3">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-gold text-navy">
              {g.type === 'skins' ? <Target size={16} /> : <Trophy size={16} />}
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-bold tracking-wide text-gold-deep">
                {g.type.toUpperCase()} · ${g.amount} · {g.scoring.toUpperCase()}
              </div>
              <div className="font-serif text-sm font-semibold">
                vs {opps.map((o) => o?.name.split(' ')[0]).join(', ')}
              </div>
            </div>
            <button type="button" onClick={() => setGames(games.filter((_, j) => j !== i))} className="p-1 text-bad">
              <X size={16} />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => setAdding(true)}
        className="flex w-full items-center justify-center gap-1.5 rounded border border-dashed border-gold bg-paper py-3 text-xs font-bold tracking-wide text-gold-deep"
      >
        <Plus size={14} /> ADD GAME
      </button>
      <p className="text-[10px] leading-relaxed text-mute">
        Opponent scores auto-generated from handicap for demo. Real version pulls from their posted round.
      </p>
      {onOpenGamesLib && (
        <p className="text-[10px] text-mute">
          {GOLF_GAMES.length} formats in library —{' '}
          <button type="button" onClick={onOpenGamesLib} className="font-bold text-gold-deep underline">open library</button>
        </p>
      )}
    </div>
  );
}
